import React from 'react';

type PaginationProps = {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  maxVisible?: number; // how many page number buttons to show at once
};

// Returns a sliding window of page numbers, e.g. with maxVisible=10:
// pages 1-10 shown while on page 4, then window shifts as you move past page 10.
const getPageWindow = (currentPage: number, totalPages: number, maxVisible: number): number[] => {
  if (totalPages <= maxVisible) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
  let end = start + maxVisible - 1;

  if (end > totalPages) {
    end = totalPages;
    start = end - maxVisible + 1;
  }

  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
};

const Pagination: React.FC<PaginationProps> = ({ currentPage, totalPages, onPageChange, maxVisible = 10 }) => {
  if (totalPages <= 1) return null;

  const pages = getPageWindow(currentPage, totalPages, maxVisible);

  return (
    <div className="flex justify-center items-center gap-1 mt-6">
      <button
        onClick={() => onPageChange(Math.max(1, currentPage - 1))}
        disabled={currentPage === 1}
        className="w-8 h-8 flex items-center justify-center rounded-md border border-[#e4e9f0] disabled:opacity-50 disabled:cursor-not-allowed"
      >
        ‹
      </button>

      {pages.map((page) => (
        <button
          key={page}
          onClick={() => onPageChange(page)}
          className={`w-8 h-8 flex items-center justify-center rounded-md text-sm ${
            currentPage === page ? 'bg-[#e84545] text-white' : 'border border-[#e4e9f0]'
          }`}
        >
          {page}
        </button>
      ))}

      <button
        onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
        disabled={currentPage === totalPages}
        className="w-8 h-8 flex items-center justify-center rounded-md border border-[#e4e9f0] disabled:opacity-50 disabled:cursor-not-allowed"
      >
        ›
      </button>
    </div>
  );
};

export default Pagination;
